import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Play } from "lucide-react";
import { WHATSAPP } from "./Nav";

const HERO_VIDEO =
    "https://videos.pexels.com/video-files/3209828/3209828-hd_1920_1080_25fps.mp4";
const HERO_POSTER =
    "https://images.unsplash.com/photo-1604608672516-f1b9b1d1f18d?fm=jpg&q=70&w=1600&auto=format&fit=crop";

export default function Hero() {
    const videoRef = useRef(null);
    const [showVideo, setShowVideo] = useState(false);

    // Defer the heavy video until the browser is idle — keeps LCP fast.
    useEffect(() => {
        const start = () => setShowVideo(true);
        if ("requestIdleCallback" in window) {
            const id = window.requestIdleCallback(start, { timeout: 1500 });
            return () => window.cancelIdleCallback?.(id);
        }
        const t = setTimeout(start, 800);
        return () => clearTimeout(t);
    }, []);

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-[100svh] pt-32 md:pt-40 pb-20 overflow-hidden"
        >
            {/* Static poster (LCP-friendly) */}
            <img
                src={HERO_POSTER}
                alt=""
                aria-hidden="true"
                fetchpriority="high"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover opacity-40"
            />

            {/* Deferred video — fades in once idle */}
            {showVideo && (
                <video
                    ref={videoRef}
                    className="absolute inset-0 w-full h-full object-cover opacity-40 transition-opacity duration-700"
                    src={HERO_VIDEO}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-hidden="true"
                />
            )}

            {/* Vignette + red wash */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/55 to-[#050505]" />
            <div className="ink-blot top-[8%] left-[-12%] w-[42rem] h-[42rem] opacity-50" />
            <div
                className="ink-blot bottom-[-15%] right-[-10%] w-[34rem] h-[34rem]"
                style={{ opacity: 0.35 }}
            />

            {/* Film grain crosshair UI */}
            <div className="absolute top-24 left-6 md:left-10 z-10 hidden md:flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/70">
                <span className="h-2 w-2 rounded-full bg-[#e6192b] blink" />
                REC · 4K · 24fps
            </div>
            <div className="absolute top-24 right-6 md:right-10 z-10 hidden md:block text-[10px] uppercase tracking-[0.3em] text-white/70 tabular-nums">
                ISO 100 · F/1.8 · 00:02:16
            </div>

            <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
                <div
                    data-testid="hero-issue-header"
                    className="flex items-center justify-between text-[11px] uppercase tracking-[0.3em] text-[#a0a0ab] mb-12 reveal"
                >
                    <span>
                        Vol. 01 · The AI-First Era ·{" "}
                        <span className="text-[#e6192b]">●</span> Live
                    </span>
                    <span className="hidden sm:block">
                        Estd. 2021 · Bengaluru, India
                    </span>
                </div>

                <div className="grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-9">
                        <div
                            data-testid="hero-overline"
                            className="text-[12px] uppercase tracking-[0.3em] text-[#e6192b] mb-6 reveal"
                        >
                            ⎯⎯ A media department, engineered.
                        </div>
                        <h1
                            data-testid="hero-headline"
                            className="font-display-tight text-[clamp(3.2rem,11vw,11rem)] text-balance reveal"
                        >
                            <span className="block">From the Pixel</span>
                            <span className="block">
                                to the{" "}
                                <span className="relative inline-block text-[#e6192b]">
                                    Podium
                                    <span className="absolute -right-3 top-2 md:top-5 inline-block w-3 h-3 md:w-5 md:h-5 bg-[#e6192b] blink" />
                                </span>
                                .
                            </span>
                        </h1>
                    </div>

                    <div className="md:col-span-3 reveal">
                        <p
                            data-testid="hero-subline"
                            className="text-[15px] md:text-base text-[#cfcfd6] leading-relaxed border-l border-white/20 pl-5"
                        >
                            We engineer brand{" "}
                            <span className="text-white">authority</span> — not
                            visibility. A hybrid of journalistic rigour and
                            generative-engine science, built for businesses
                            operating in the AI-First Era.
                        </p>
                    </div>
                </div>

                <div className="mt-14 flex flex-col sm:flex-row sm:items-center gap-5 reveal">
                    <a
                        href={WHATSAPP}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="hero-cta-primary"
                        className="group inline-flex items-center gap-3 bg-[#e6192b] hover:bg-[#ff2a3d] text-white px-7 py-4 rounded-full text-[12px] uppercase tracking-[0.22em] font-medium transition-all hover:scale-[1.02]"
                    >
                        Book a Discovery Call
                        <ArrowUpRight
                            size={16}
                            className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        />
                    </a>
                    <a
                        href="#ecosystem"
                        data-testid="hero-cta-secondary"
                        className="group inline-flex items-center gap-3 border border-white/30 hover:border-white text-white px-7 py-4 rounded-full text-[12px] uppercase tracking-[0.22em] font-medium transition-colors backdrop-blur-sm bg-white/5"
                    >
                        <Play size={14} className="text-[#e6192b]" />
                        Explore the Ecosystem
                    </a>
                </div>

                <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-white/15 reveal">
                    {[
                        ["Reputation Capital", "engineered, not bought"],
                        ["GEO", "co-cited in ChatGPT, Claude, Perplexity"],
                        ["High-Ticket B2B", "for complex sales cycles"],
                        ["Hybrid Studio", "AI efficiency · human integrity"],
                    ].map(([h, s]) => (
                        <div key={h}>
                            <div className="text-[11px] uppercase tracking-[0.22em] text-[#e6192b]">
                                {h}
                            </div>
                            <div className="mt-1 text-[14px] text-[#cfcfd6]">
                                {s}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
